<template>
  <div v-if="options && options.meta" class="data-grid" ref="grid">
    <div v-if="!editView">
      <AddColumnBtn v-if="!options.meta && canUpdate" @showAddColumnModal="showAddColumnModal" />

      <div class="data-grid__table-wrapper">
        <div class="data-grid__header flex_wrap">
          <div class="data-grid__header_add">
            <b-button
              v-if="
                !hideAddRecord &&
                  options.meta &&
                  (!inherited || !lockedForInherited)
              "
              variant="success"
              size="sm"
              @click.prevent="addRecord"
              class="data-grid__addRecordTrigger"
              :disabled="addRowInProcess"
            >
              <icon name="plus" style="vertical-align: -4px;" />
              <span>Add {{ singular(options.title) }}</span>
            </b-button>

            <h3>
              <span
                v-if="!fbField"
                :contenteditable="
                  editableGridTitle && (!inherited || !lockedForInherited)
                "
                :ref="`data-grid-title-${gridId}-${widgetKey}`"
                @click="editGridTitle(`data-grid-title-${gridId}-${widgetKey}`)"
                @blur="saveGridTitle(`data-grid-title-${gridId}-${widgetKey}`)"
                @keypress.enter.prevent="
                  blurGridTitleInput(`data-grid-title-${gridId}-${widgetKey}`)
                "
                @keydown.esc="
                  resetGridTitleInput(`data-grid-title-${gridId}-${widgetKey}`)
                "
              >{{ gridTitle }}</span>
              <ViewSelector
                v-if="!fbField && currentTemplate !== 'core' && canUpdate"
                :gridId="gridId"
                :view="view"
                :widgetKey="widgetKey"
                :inherited="inherited"
              />
            </h3>
          </div>

          <div
            class="data-grid__controls flex_wrap"
            style="position:sticky; right:0px; z-index: 100;"
            v-if="!hideToolbar"
          >
            <span v-if="!inherited || !lockedForInherited" class="data-grid__controlSwiter">
              <span>Edit Mode:</span>
              <label class="custom-toggle">
                <input type="checkbox" v-model="editMode" />
                <span
                  class="custom-toggle-slider rounded-circle"
                  data-label-off="No"
                  data-label-on="Yes"
                ></span>
              </label>
            </span>
            <span class="data-grid__itemsPerPageControl">
              <span>Rows per page:</span>
              <b-form-select v-model="itemsPerPage" size="sm" aria-invalid="true">
                <option :value="10">10</option>
                <option :value="15">15</option>
                <option :value="20">20</option>
                <option :value="30">30</option>
                <option :value="40">40</option>
                <option :value="50">50</option>
                <option :value="60">60</option>
              </b-form-select>
            </span>

            <div
              v-if="!inherited || !lockedForInherited"
              class="data-grid__protectedControls flex_wrap"
            >
              <span v-if="!fbField && canUpdate">
                <AddColumnBtn @showAddColumnModal="showAddColumnModal" />
              </span>

              <span v-if="!fbField && canUpdate">
                <b-popover
                  :target="`columns-visibility-${widgetKey}`"
                  triggers="focus"
                  placement="bottom"
                >
                  <div v-for="(col, cellKey) in cellsMeta" :key="cellKey">
                    <b-form-checkbox
                      :checked="col.invisible"
                      @change="columnVisibilityChanged($event, cellKey)"
                    >
                      <span>{{ col.displayName }}</span>
                    </b-form-checkbox>
                  </div>
                </b-popover>
                <b-button
                  style="box-shadow:none;"
                  size="sm"
                  variant="primary"
                  :id="`columns-visibility-${widgetKey}`"
                  class="hideColumns"
                >
                  <icon name="columns" style="vertical-align: -3px;" />
                </b-button>
              </span>

              <span v-if="!fbField && (!inherited || (view && extended)) && canUpdate">
                <b-button
                  style="box-shadow:none;"
                  size="sm"
                  variant="primary"
                  @click="openFilterBuilder"
                  class="openFilterBuilder"
                >
                  <icon name="search" />
                </b-button>
              </span>
              <span>
                <b-button
                  size="sm"
                  variant="primary"
                  style="box-shadow:none;"
                  @click="exportToExcel"
                >
                  <icon name="download" />
                </b-button>
              </span>
              <span>
                <b-button
                  size="sm"
                  variant="primary"
                  style="box-shadow:none;"
                  @click="showUploadRowsModal"
                >
                  <icon name="upload" />
                </b-button>
              </span>
              <span>
                <b-button
                  size="sm"
                  variant="primary"
                  style="box-shadow:none;"
                  @click="$refs.uploadExcelModal.open()"
                >
                  <i class="fas fa-file-excel"></i>
                </b-button>
              </span>
              <span class="data-grid__connectToGSheets" v-if="!fbField">
                <GoogleSheets :options="options" :gridId="gridId"></GoogleSheets>
              </span>
              <span v-if="canUpdate">
                <a
                  href="#"
                  @click.prevent="$refs.settingsModal.open()"
                  v-b-tooltip
                  title="Settings"
                >
                  <icon name="cogs" scale="1.3" />
                </a>
              </span>
            </div>
          </div>
        </div>
        <div class="data-grid__tHeadToggle" :class="{ _active: headingShown }">
          <b-button
            style="box-shadow:none;"
            variant="primary"
            size="sm"
            @click="headingShown = !headingShown"
          >{{ headingShown ? 'Hide' : 'Show' }} heading</b-button>
        </div>
        <div v-if="filterBuilder && !fbField">
          <TableHeadingFilterBuilder
            :filter="filterBuilder"
            :gridId="gridId"
            @change="applyFilterBuilder(filterBuilder)"
          />
        </div>
      </div>

      <HandyScroll ref="handyScroll">
        <table class="table data-grid__table stacked-table">
          <thead
            v-dg-draggable-th="cellsMeta"
            class="data-grid__tHead"
            :class="{ _hidden: !headingShown }"
          >
            <th
              class="data-grid__emptyRowHeading data-grid__tableHeading"
              :class="{
                _filterOn: filterOn,
                _hasValues: filterHasValues,
              }"
            >
              <a
                v-if="
                  !filterHasValues &&
                    !options.filterVisible &&
                    !fbField &&
                    !filterBuilder
                "
                href="#"
                @click.prevent="toggleFilter"
                :id="`data-grid-filter-${gridId}-${widgetKey}`"
                class="data-grid__filterToggle btn-sm btn-primary"
                style="box-shadow:none !important"
              >Filter</a>
              <a
                v-show="filterHasValues && !fbField && !filterBuilder"
                href="#"
                @click.prevent="clearFilter"
                class="data-grid__clearFilter btn-sm btn-info"
                title="Clear Filter"
              >Clear</a>
            </th>
            <th class="data-grid__tableHeading">
              <div class="heading__content">
                <span class="data-grid__columnNameVal">ID</span>
                <span>
                  <a
                    href="#"
                    v-b-tooltip
                    title="Sort"
                    v-if="sortCol !== '_id'"
                    @click.prevent="applySort('_id', 'asc')"
                  >
                    <icon name="sort" scale=".8" />
                  </a>
                  <a
                    href="#"
                    v-if="sortCol === '_id' && sortDirection === 'asc'"
                    @click.prevent="applySort('_id', 'desc')"
                  >
                    <icon name="sort-asc" scale=".8" />
                  </a>
                  <a
                    href="#"
                    v-if="sortCol === '_id' && sortDirection === 'desc'"
                    @click.prevent="applySort('_id', 'asc')"
                  >
                    <icon name="sort-desc" scale=".8" />
                  </a>
                </span>
              </div>
            </th>
            <TableHeading
              v-for="(data, cellKey) in headingFiltered"
              v-show="!cellsMeta[cellKey].invisible"
              :key="'heading-' + cellKey"
              :displayName="data.displayName"
              :type="data.type"
              :cellKey="cellKey"
              :gridId="gridId"
              :widgetKey="widgetKey"
              :options="options"
              :sortCol="sortCol"
              :sortDirection="sortDirection"
              :filterOn="filterOn"
              :filter="filter"
              :filterBuilder="filterBuilder"
              :gridData="gridData"
              @onSort="applySort"
              @filterItemRemove="onFilterItemRemove"
              :style="
                cellsMeta[cellKey].minWidth && {
                  minWidth: getColumnMinWidth(cellsMeta[cellKey].minWidth),
                }
              "
            />
            <th class="data-grid__tableHeading _remove"></th>
          </thead>
          <tbody v-if="cellsMeta && gridData">
            <TableRow
              v-for="row in gridReady"
              :key="row.rowKey"
              :options="options"
              :rowKey="row.rowKey"
              :gridId="gridId"
              :widgetKey="widgetKey"
              :editMode="editMode"
              :fbField="fbField"
              :inherited="inherited"
              :lockedForInherited="lockedForInherited"
            >
              <TableCell
                v-for="cellKey in cellsFiltered"
                :key="row.rowKey + '-' + cellKey"
                v-show="!cellsMeta[cellKey].invisible"
                class="data-grid__cell"
                :data-header="cellsMeta[cellKey].displayName"
                :gridId="gridId"
                :rowKey="row.rowKey"
                :cellKey="cellKey"
                :options="options"
              >
                <TextCell
                  v-if="cellsMeta[cellKey].type === 'string'"
                  :gridId="gridId"
                  :rowKey="row.rowKey"
                  :cellKey="cellKey"
                  :cellVal="row[cellKey]"
                  :options="options"
                  :widgetKey="widgetKey"
                  :editMode="editMode"
                  :fbField="fbField"
                  :controlInfo="getFieldControlInfo(cellKey)"
                />
                <MultilineTextCell
                  v-else-if="cellsMeta[cellKey].type === 'multiline_text'"
                  :rowKey="row.rowKey"
                  :cellKey="cellKey"
                  :cellVal="row[cellKey]"
                  :options="options"
                  :gridId="gridId"
                  :widgetKey="widgetKey"
                  :editMode="editMode"
                  :controlInfo="getFieldControlInfo(cellKey)"
                />
                <NumberCell
                  v-if="cellsMeta[cellKey].type === 'number'"
                  :gridId="gridId"
                  :rowKey="row.rowKey"
                  :cellKey="cellKey"
                  :cellVal="row[cellKey]"
                  :options="options"
                  :widgetKey="widgetKey"
                  :editMode="editMode"
                  :fbField="fbField"
                  :controlInfo="getFieldControlInfo(cellKey)"
                />
                <DateCell
                  v-else-if="cellsMeta[cellKey].type === 'date'"
                  :rowKey="row.rowKey"
                  :cellKey="cellKey"
                  :cellVal="row[cellKey]"
                  :cellDisplayVal="row[`$${cellKey}$display`]"
                  :options="options"
                  :gridId="gridId"
                  :widgetKey="widgetKey"
                  :editMode="editMode"
                  :controlInfo="getFieldControlInfo(cellKey)"
                />
                <LookUpCell
                  v-else-if="cellsMeta[cellKey].type === 'lookup'"
                  :rowKey="row.rowKey"
                  :cellKey="cellKey"
                  :cellVal="row[cellKey]"
                  :cellDisplayVal="row[`$${cellKey}$display`]"
                  :options="options"
                  :gridId="gridId"
                  :widgetKey="widgetKey"
                  :editMode="editMode"
                  :controlInfo="getFieldControlInfo(cellKey)"
                />
                <MultiLookUpCell
                  v-else-if="cellsMeta[cellKey].type === 'multi_lookup'"
                  :rowKey="row.rowKey"
                  :cellKey="cellKey"
                  :cellVal="row[cellKey]"
                  :cellDisplayVal="row[`$${cellKey}$display`]"
                  :options="options"
                  :gridId="gridId"
                  :widgetKey="widgetKey"
                  :editMode="editMode"
                  :controlInfo="getFieldControlInfo(cellKey)"
                />
                <FilesCell
                  v-else-if="cellsMeta[cellKey].type === 'files'"
                  :rowKey="row.rowKey"
                  :cellKey="cellKey"
                  :cellVal="row[cellKey]"
                  :cellDisplayVal="row[`$${cellKey}$display`]"
                  :options="options"
                  :gridId="gridId"
                  :widgetKey="widgetKey"
                  :editMode="editMode"
                  :controlInfo="getFieldControlInfo(cellKey)"
                />
                <CheckboxCell
                  v-else-if="cellsMeta[cellKey].type === 'checkbox'"
                  :rowKey="row.rowKey"
                  :cellKey="cellKey"
                  :cellVal="row[cellKey]"
                  :options="options"
                  :gridId="gridId"
                  :widgetKey="widgetKey"
                  :editMode="editMode"
                  :controlInfo="getFieldControlInfo(cellKey)"
                />
                <UserCell
                  v-else-if="cellsMeta[cellKey].type === 'user'"
                  :rowKey="row.rowKey"
                  :cellKey="cellKey"
                  :cellVal="row[cellKey]"
                  :cellDisplayVal="row[`$${cellKey}$display`]"
                  :options="options"
                  :gridId="gridId"
                  :widgetKey="widgetKey"
                  :editMode="editMode"
                  :controlInfo="getFieldControlInfo(cellKey)"
                />
                <CustomCell
                  v-else-if="cellsMeta[cellKey].type === 'custom'"
                  :rowKey="row.rowKey"
                  :cellKey="cellKey"
                  :options="options"
                  :gridId="gridId"
                  :widgetKey="widgetKey"
                  :editMode="editMode"
                  :dInst="dInst"
                  :controlInfo="getFieldControlInfo(cellKey)"
                />
                <ProfileImageCell
                  v-else-if="cellsMeta[cellKey].type === 'profile_image'"
                  :rowKey="row.rowKey"
                  :cellKey="cellKey"
                  :cellVal="row[cellKey]"
                  :options="options"
                  :gridId="gridId"
                  :widgetKey="widgetKey"
                  :editMode="editMode"
                  :controlInfo="getFieldControlInfo(cellKey)"
                />
                <SelectCell
                  v-else-if="cellsMeta[cellKey].type === 'select'"
                  :rowKey="row.rowKey"
                  :cellKey="cellKey"
                  :cellVal="row[cellKey]"
                  :options="options"
                  :gridId="gridId"
                  :widgetKey="widgetKey"
                  :editMode="editMode"
                  :controlInfo="getFieldControlInfo(cellKey)"
                />
                <TextEditor
                  v-else-if="cellsMeta[cellKey].type === 'text_editor'"
                  :rowKey="row.rowKey"
                  :cellKey="cellKey"
                  :cellVal="row[cellKey]"
                  :options="options"
                  :gridId="gridId"
                  :widgetKey="widgetKey"
                  :editMode="editMode"
                  :controlInfo="getFieldControlInfo(cellKey)"
                />
                <div v-else-if="cellsMeta[cellKey].type === 'workflow'">
                  <span style="display:none">{{ row }}</span>
                  <WorkflowButtons
                    :data="row"
                    :firstStep="cellsMeta[cellKey].startTitle"
                    :workflow="cellsMeta[cellKey].workflow"
                    :gridId="row.nextEvents"
                  />
                </div>
              </TableCell>
            </TableRow>
          </tbody>
          <tbody v-else>
            <tr>
              <td style="padding-bottom: 3px;">
                <h3 style="margin-bottom: 0;">No rows</h3>
              </td>
            </tr>
          </tbody>
        </table>
      </HandyScroll>

      <div class="mt-4">
        <paginate
          v-if="pagesCount > 1"
          :page-count="pagesCount"
          :value="currentPage"
          container-class="pagination b-pagination"
          page-class="page-item"
          page-link-class="page-link"
          prev-class="page-item"
          prev-link-class="page-link"
          next-class="page-item"
          next-link-class="page-link"
          prev-text="«"
          next-text="»"
          :click-handler="pageClickHandler"
        />
      </div>

      <OnChangeHandlerModal
        v-if="canUpdate"
        :gridId="gridId"
        :options="options"
        :widgetKey="widgetKey"
      />
      <LookUpFilterModal
        v-if="canUpdate"
        :gridId="gridId"
        :widgetKey="widgetKey"
        :options="options"
      />
      <ChangeColumnType v-if="canUpdate" :gridId="gridId" :options="options" />
      <FilterBuilderModal
        v-if="canUpdate"
        :gridId="gridId"
        :widgetKey="widgetKey"
        :options="options"
        :group="JSON.parse(this.getFilterBuilderString(true))"
        @save="saveFilterBuilder($event)"
      />
      <AddColumnModal v-if="canUpdate" ref="addColumnModal" :gridId="gridId" />
      <ColWidthModal v-if="canUpdate" :widgetKey="widgetKey" :gridId="gridId" />
      <AddGridRows @addGridRows="addGridRows" />
      <UploadExcelModal :gridId="gridId" ref="uploadExcelModal" />
      <Settings v-if="canUpdate" ref="settingsModal" :gridId="gridId" :options="options" />

      <div v-if="!inherited || !lockedForInherited" style="background-color:white">
        <div>
          <b-button
            v-if="
              (canUpdate || !hideAddRow) &&
                options.meta
            "
            variant="success"
            size="sm"
            @click.prevent="addRow"
            class="data-grid__addRowTrigger"
            :disabled="addRowInProcess"
          >
            <icon name="plus" style="vertical-align: -4px;" />
            <span>Add Row</span>
          </b-button>
          <b-button
            v-if="
              (canUpdate || !hideAddRecord) &&
                options.meta
            "
            variant="primary"
            size="sm"
            @click.prevent="addRecord"
            class="data-grid__addRecordTrigger"
            :disabled="addRowInProcess"
          >
            <icon name="plus" style="vertical-align: -4px;" />
            <span>Add {{ singular(options.title) }}</span>
          </b-button>
        </div>
      </div>
    </div>

    <div
      v-if="editView.dgEditMode && (!inherited || !lockedForInherited)"
      style="background-color:white"
    >
      <div>
        <div class="data-grid__goBackToGridMode">
          <b-button variant="primary" size="sm" @click="resetEditView">
            <icon name="long-arrow-left" />
            <span>Go Back</span>
          </b-button>
        </div>

        <div>
          <EditRowForm
            :gridId="editView.gridId"
            :rowKey="editView.rowKey"
            :view="view"
            :dInst="dInst"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { orderBy, cloneDeep, debounce, findIndex, toArray } from 'lodash'
import alasql from 'alasql'
import paginateArray from 'paginate-array'
import XLSX from 'xlsx'
import moment from 'moment'
// services
import db from '@/services/db'
// components
import AddColumnBtn from './controls/AddColumnBtn'
import TableHeading from './table/TableHeading'
import TableHeadingFilterBuilder from './table/TableHeadingFilterBuilder'
import TableRow from './table/TableRow'
import TableCell from './table/TableCell'
import EditRowForm from './forms/EditRow'
import GoogleSheets from './google-sheets/GoogleSheets'
import ViewSelector from './ViewSelector'
// modals
import OnChangeHandlerModal from './modals/OnChangeHandler'
import LookUpFilterModal from './modals/LookUpFilter'
import FilterBuilderModal from './modals/FilterBuilder'
import AddColumnModal from './modals/AddColumn'
import ColWidthModal from './modals/ColWidth'
import AddGridRows from './modals/AddGridRows'
import Settings from './modals/Settings'
import UploadExcelModal from './modals/UploadExcelModal'
import ChangeColumnType from './modals/ChangeColumnType'
// cells
import TextCell from './table/TableCell/TextCell'
import LookUpCell from './table/TableCell/LookUpCell'
import MultiLookUpCell from './table/TableCell/MultiLookUpCell'
import DateCell from './table/TableCell/DateCell'
import FilesCell from './table/TableCell/FilesCell'
import CheckboxCell from './table/TableCell/CheckboxCell'
import UserCell from './table/TableCell/UserCell'
import MultilineTextCell from './table/TableCell/MultilineTextCell'
import CustomCell from './table/TableCell/CustomCell'
import ProfileImageCell from './table/TableCell/ProfileImageCell'
import SelectCell from './table/TableCell/SelectCell'
import NumberCell from './table/TableCell/NumberCell'
import WorkflowButtons from '@/components/WorkflowButtons'
import TextEditor from './table/TableCell/TextEditor'
// helpers
import utils from '@/helpers/utils/index.js'
import filterUtils from '@/helpers/filter'
import eventBus from '@/helpers/event-bus'
import config from '@/config'
import axios from 'axios'
import { singular, plural } from 'pluralize-me'

const functionsUrl = config.midServer[process.env.APP_CONF.instance]

export default {
  name: 'DataGrid',
  props: {
    tabs: Array,
    widgets: Object,
    gridId: String,
    view: String,
    widgetKey: String,
    inherited: Boolean,
    extended: Boolean,
    hiddenCols: {
      type: Array,
      default: () => [],
    },
    defaultFilter: {
      type: Object,
      default: () => ({}),
    },
    defaultFilterBuilder: {
      type: Object,
      default: null,
    },
    defaultHiddenVals: {
      type: Object,
      default: () => ({}),
    },
    inEditMode: {
      type: Boolean,
      default: false,
    },
    hideAddRow: {
      type: Boolean,
      default: false,
    },
    hideAddRecord: {
      type: Boolean,
      default: false,
    },
    fbField: {
      type: String,
      default: '',
    },
    hideToolbar: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    AddColumnBtn,
    TableHeading,
    TableHeadingFilterBuilder,
    TableRow,
    TableCell,
    EditRowForm,
    ViewSelector,
    // cells
    TextCell,
    LookUpCell,
    MultiLookUpCell,
    DateCell,
    FilesCell,
    CheckboxCell,
    UserCell,
    MultilineTextCell,
    CustomCell,
    ProfileImageCell,
    SelectCell,
    NumberCell,
    WorkflowButtons,
    TextEditor,
    // modals
    OnChangeHandlerModal,
    LookUpFilterModal,
    FilterBuilderModal,
    AddColumnModal,
    ColWidthModal,
    GoogleSheets,
    AddGridRows,
    Settings,
    UploadExcelModal,
    ChangeColumnType,
  },
  data() {
    return {
      dataForRowEditLoaded: true,
      filteredData: null,
      gridReady: null,
      gridSorted: null,
      // grid title edit
      editableGridTitle: null,
      lastKnownGridTitle: '',
      resetGridTitleEditFlag: false,
      sortCol: null,
      sortDirection: null,
      filterOn: false,
      filter: this.defaultFilter,
      filterBuilder: this.defaultFilterBuilder,
      dInst: null,
      headingShown: false,
      pagesCount: 1,
      currentPage: 1,
      itemsPerPage: 10,
      addRowInProcess: false,
      sortAndFilterGridWithDebounce: debounce(this.sortAndFilterGrid, 100),
      inheritedOptions: null,
      sortByView: {},
    }
  },
  computed: {
    ...mapState([
      'currentDash',
      'currentTab',
      'currentSubTab',
      'dataGrid_options',
      'models_dashboardModel',
      'dgInEditMode',
      'workflows',
      'dataGrid_filterBuilders',
      'currentTemplate',
      'userList',
      'options',
      'dashboardSettings',
    ]),
    gridData() {
      let result = $getGrid(this.gridId)

      return result
    },
    options() {
      this.updateHandyScroll()
      if (!this.dataGrid_options) return {}
      return this.dataGrid_options[this.gridId]
    },
    cellsMeta() {
      let meta
      const metaReady = cloneDeep({})
      const gridMeta = cloneDeep(this.dataGrid_options[this.gridId].meta)

      if (this.viewExists) {
        meta = cloneDeep([])

        try {
          const viewMeta = this.dataGrid_options[this.gridId].views[this.view]
            .meta

          for (let key in gridMeta) {
            if (!viewMeta[key]) continue

            const item = gridMeta[key]
            item.key = key
            item.invisible = viewMeta[key].invisible ? true : false
            meta[viewMeta[key].order] = item
          }
        } catch (err) {
          console.error(err)

          for (let key in gridMeta) {
            if (!viewMeta[key]) continue

            const item = gridMeta[key]
            item.key = key
            item.invisible = item.invisible ? true : false
            meta[item.order] = item
          }
        }
      } else {
        meta = cloneDeep([])

        for (let key in gridMeta) {
          const item = gridMeta[key]
          item.key = key
          item.invisible = item.invisible ? true : false
          meta[item.order] = item
        }
      }

      for (let item of meta) {
        if (!item) continue
        if (item.type === 'datagrid') continue
        metaReady[item.key] = item
        delete metaReady[item.key].key
      }

      return metaReady
    },
    viewExists() {
      if (!this.dataGrid_options[this.gridId].views) return false
      if (!this.dataGrid_options[this.gridId].views[this.view]) return false
      return true
    },
    headingFiltered() {
      const heading = {}

      for (let cellKey in this.cellsMeta) {
        if (this.fbField && this.fbField === cellKey) continue
        if (!this.hiddenCols.includes(cellKey) && this.cellsMeta[cellKey])
          heading[cellKey] = this.cellsMeta[cellKey]
      }

      return heading
    },
    cellsFiltered() {
      const cells = []

      for (let cellKey in this.cellsMeta) {
        if (this.fbField && this.fbField === cellKey) continue
        if (!this.hiddenCols.includes(cellKey)) cells.push(cellKey)
      }

      return cells
    },
    filterHasValues() {
      return this.$count(this.filter) > 0 ? true : false
    },
    gridsTitles() {
      const titles = {}

      for (let gridId in this.dataGrid_options) {
        titles[gridId] = this.dataGrid_options[gridId].title
      }

      return titles
    },
    editView() {
      const query = this.$route.query

      if (!query.dgEditMode) return false
      if (!query.gridId) return false
      if (query.gridId !== this.gridId) return false

      const viewData = {
        dgEditMode: query.dgEditMode,
        gridId: query.gridId,
        rowKey: query.rowKey,
      }

      return viewData
    },
    editMode: {
      get() {
        this.$nextTick(() => {
          this.updateHandyScroll()
        })

        if (this.dgInEditMode[this.gridId]) return true
        return false
      },
      set() {
        this.$store.commit('toggleDGEditMode', this.gridId)
      },
    },
    gridTitle() {
      try {
        if (this.viewExists) return this.options.views[this.view].title

        return this.options.title
      } catch (err) {
        console.error(err)

        return this.options.title
      }
    },
    lockedForInherited() {
      return this.options.lockedForInherited || false
    },
    canUpdate() {
      return this.$can('update', 'Dashboard_' + this.currentDash)
    },
  },
  watch: {
    filter: {
      handler() {
        this.sortAndFilterGridWithDebounce()
      },
      deep: true,
    },
    defaultFilter: {
      deep: true,
      handler() {
        this.filter = Object.assign(this.filter, this.defaultFilter)
      },
    },
    pagesCount() {
      if (this.currentPage > this.pagesCount)
        this.pageClickHandler(this.pagesCount)
    },
    itemsPerPage() {
      this.sortAndFilterGridWithDebounce()
    },
    view() {
      const filterBuilderString = this.getFilterBuilderString(true)

      try {
        this.applyFilterBuilder(JSON.parse(filterBuilderString))
      } catch (err) {
        console.error(err)
      }
    },
    currentTab() {
      this.initGridData()
    },
    currentSubTab() {
      this.initGridData()
    },
  },
  methods: {
    async exportToExcel() {
      const data = this.getDataToExport()

      var tableWS = XLSX.utils.json_to_sheet(orderBy(data, ['_id']))

      var wb = XLSX.utils.book_new()

      XLSX.utils.book_append_sheet(wb, tableWS, 'table')

      XLSX.writeFile(wb, `${this.gridId}.xlsx`)
    },
    getDataToExport() {
      try {
        const result = []

        if (!this.filteredData) return []

        for (let idx = 0; idx < this.filteredData.length; idx++) {
          const row = this.filteredData[idx]
          const rowReady = {}

          for (let cellKey in this.cellsMeta) {
            switch (this.cellsMeta[cellKey].type) {
              case 'date':
                if (!row[cellKey]) {
                  rowReady[cellKey] = ''
                  break
                }

                const date = moment(row[cellKey]).format(
                  this.dashboardSettings.regional.date
                )

                rowReady[cellKey] = typeof date === 'string' ? date : ''
                break
              case 'lookup':
                rowReady[cellKey] = row[`$${cellKey}$display`] || ''
                break
              case 'multi_lookup':
                rowReady[cellKey] = (
                  toArray(row[`$${cellKey}$display`]) || []
                ).join(',')
                break
              default:
                rowReady[cellKey] = row[cellKey] || ''
            }
          }

          result.push(
            Object.assign(
              {
                rowKey: row.rowKey || '',
                _id: row._id || '',
              },
              rowReady
            )
          )
        }

        return result
      } catch (err) {
        console.error(err)

        return null
      }
    },
    openFilterBuilder() {
      const gridMeta = this.dataGrid_options[this.gridId].meta
      const keys = Object.keys(gridMeta || {})
      eventBus.$emit(`showFilterBuilder${this.widgetKey}-${this.gridId}`, {
        keys,
      })
    },
    applyFilterBuilder(filterBuilder) {
      if (filterBuilder && !this.fbField) {
        try {
          this.$store.commit('dataGrid_setFilterBuilder', {
            widgetKey: this.widgetKey,
            filter: JSON.stringify(filterBuilder),
          })
        } catch (err) {
          console.error(err)
        }

        this.filterBuilder =
          filterBuilder.rules && filterBuilder.rules.length > 0
            ? filterBuilder
            : null
        this.filter = filterUtils.filterBuilderToFilter(filterBuilder)
      }
      this.sortAndFilterGridWithDebounce()
    },
    saveFilterBuilder(filterBuilder) {
      const data = {
        gridId: this.gridId,
        filterBuilder,
      }

      if (this.viewExists) data.view = this.view

      db.dataGrid.options.saveFilterBuilder(data)

      this.applyFilterBuilder(filterBuilder)
    },
    sortAndFilterGrid() {
      this.$nextTick(() => {
        this.filteredData = filterUtils.filterData(
          this.gridData,
          this.filter,
          this.cellsMeta
        )

        let gridData = this.sortGrid(this.filteredData)

        this.gridSorted = gridData

        // paginate
        this.pagesCount = Math.ceil(this.$count(gridData) / this.itemsPerPage)

        gridData = this.paginate(gridData)

        this.gridReady = gridData
      })
    },
    paginate(gridData) {
      const paginateCollection = paginateArray(
        gridData,
        this.currentPage,
        this.itemsPerPage
      )

      return paginateCollection.data
    },
    pageClickHandler(currentPage) {
      this.currentPage = currentPage || 1

      let gridData = this.sortGrid(this.filteredData)
      this.gridReady = this.paginate(gridData)
    },
    sortGrid(dataToSort) {
      if (
        Object.keys(this.sortByView).length !== 0 &&
        this.sortByView[this.view] !== undefined
      ) {
        this.sortCol = this.sortByView[this.view].sortCol
        this.sortDirection = this.sortByView[this.view].sortDirection
      } else {
        this.sortCol = null
        this.sortDirection = null
      }

      const complexTypes = ['lookup', 'user']
      const sortKey = complexTypes.includes(this.sortCol)
        ? `$${this.sortCol}$display`
        : this.sortCol

      return orderBy(dataToSort, [sortKey], [this.sortDirection])
    },
    editGridTitle(key) {
      if (this.editableGridTitle) return

      this.lastKnownGridTitle = this.gridsTitles[this.gridId]
      this.editableGridTitle = key

      this.$nextTick(() => {
        this.$refs[key].focus()
        this.$cursorToEnd(this.$refs[key])
      })
    },
    saveGridTitle(key) {
      if (this.resetGridTitleEditFlag) {
        this.resetGridTitleEditFlag = false
        return
      }

      const newVal = this.$refs[key].textContent.trim()

      this.lastKnownGridTitle = ''
      this.editableGridTitle = null

      if (!newVal) {
        this.resetGridTitleInput(key)
        return
      }
      if (newVal === this.lastKnownGridTitle) return

      const data = {
        gridId: this.gridId,
        value: newVal,
      }

      if (this.viewExists) data.view = this.view

      db.dataGrid.options.editTitle(data)
    },
    blurGridTitleInput(key) {
      this.$refs[key].blur()
    },
    resetGridTitleInput(key) {
      this.resetGridTitleEditFlag = true
      this.$refs[key].blur()
      this.$refs[key].textContent = this.lastKnownGridTitle.trim()
      this.lastKnownGridTitle = ''
      this.editableGridTitle = null
    },
    showAddColumnModal(type) {
      this.$refs.addColumnModal.open(type)
    },
    showUploadRowsModal() {
      eventBus.$emit('addGridRows')
    },
    async lookupAction(elem, item) {
      let lookUpTable = this.cellsMeta[item].lookUpTable
      let displayColumn = this.dataGrid_options[lookUpTable].displayColumn

      let getGrid = $getGrid(lookUpTable)
      let lookupValue = getGrid.find(
        lookup => lookup[displayColumn] === elem[item]
      )
      if (lookupValue) {
        return lookupValue.rowKey
      } else {
        let itemToCreate = {}
        itemToCreate[displayColumn] = elem[item].trim()
        let rowKey = await $dgAddRow(lookUpTable, itemToCreate).then(rowKey => {
          let getGrid = $getGrid(lookUpTable)
          let lookupValue = getGrid.find(
            lookup => lookup[displayColumn] === elem[item]
          )
          return lookupValue.rowKey
        })
        return rowKey
      }
    },
    multiLookupIterator(multiElem, getGrid, displayColumn, lookUpTable) {
      return new Promise((resolve, reject) => {
        let lookupObj = getGrid.find(
          lookup => lookup[displayColumn] === multiElem
        )
        if (lookupObj) {
          resolve(lookupObj.rowKey)
        } else {
          let itemToCreate = {}
          itemToCreate[displayColumn] = multiElem
          $dgAddRow(lookUpTable, itemToCreate).then(rowKey => {
            let getGrid = $getGrid(lookUpTable)
            let lookupValue = getGrid.find(
              lookup => lookup[displayColumn] === multiElem
            )
            resolve(lookupValue.rowKey)
          })
        }
      })
    },
    async multiLookupAction(elem, item) {
      let lookUpTable = this.cellsMeta[item].lookUpTable
      let multiLoockupsItems = elem[item].split(',')
      let getGrid = $getGrid(this.cellsMeta[item].lookUpTable)
      let displayColumn = this.dataGrid_options[lookUpTable].displayColumn
      let lookupValue = ''

      for (let index = 0; index < multiLoockupsItems.length; index++) {
        lookupValue += `${await this.multiLookupIterator(
          multiLoockupsItems[index].trim(),
          getGrid,
          displayColumn,
          lookUpTable
        )},`
        if (index === multiLoockupsItems.length - 1) {
          return lookupValue.slice(0, lookupValue.lastIndexOf(','))
        }
      }
    },
    async addGridRows(data) {
      if (data.length) {
        for (let i = 0; i < data.length; i++) {
          await this.addRow(data[i])
        }
      }
    },
    async createRowVals(gridId, data) {
      let rowVals = {}
      let metaTarget = this.viewExists
        ? this.dataGrid_options[gridId].views[this.view].meta
        : this.dataGrid_options[gridId].meta
      if (data && data.length) {
        for (let item in metaTarget) {
          // if (!this.headingFiltered[item].invisible) {
          let isData = data[metaTarget[item].order]
          let meta = this.cellsMeta[item]
          let lookupObj = {}

          if (!meta) continue
          let getGrid = {}
          if (meta.type === 'lookup' || meta.type === 'multi_lookup') {
            getGrid = $getGrid(meta.lookUpTable)
          }
          if (meta.type === 'lookup' && isData) {
            for (let lookup in getGrid) {
              if (
                getGrid[lookup].name &&
                getGrid[lookup].name.toUpperCase() === isData.toUpperCase()
              ) {
                rowVals[item] = getGrid[lookup].rowKey
              } else {
                lookupObj[item] = isData
                rowVals[item] = await this.lookupAction(lookupObj, item)
              }
            }
          } else if (meta.type === 'multi_lookup') {
            lookupObj[item] = isData
            rowVals[item] = await this.multiLookupAction(lookupObj, item)
          } else if (isData) {
            rowVals[item] = isData
          }
          // }
        }
      }
      return rowVals
    },
    async addRow(data, id, cellKey) {
      let gridId = id && typeof id === 'string' ? id : this.gridId
      this.addRowInProcess = true
      let rowVals = []
      if (cellKey) {
        rowVals[cellKey] = data[0]
      } else {
        rowVals = await this.createRowVals(gridId, data)
      }
      return $dgAddRow(gridId, rowVals)
        .then(async rowKey => {
          this.addRowInProcess = false

          this.$store.commit('toggleDGRowEditMode', gridId + '.' + rowKey)

          eventBus.$emit(`dgRowAdded-${gridId}`, rowKey)
          eventBus.$emit(`fbRowAdded-${gridId}`, rowKey)

          this.insertDefaultVals(rowKey)
          this.addWorkflow(rowKey)

          this.pageClickHandler(this.pagesCount)

          setTimeout(() => {
            this.updateHandyScroll()
          }, 2000)

          return rowKey
        })
        .catch(() => {
          this.addRowInProcess = false
        })
    },
    addWorkflow(rowKey) {
      var gridId = this.gridId

      //check if there is a create event for this process
      var wfs = this.workflows

      var callWf = this.callWf

      Object.keys(wfs).forEach(function(key) {
        //console.log('workflow',wfs[key])
        var wf = wfs[key]
        var grid = null

        if (wf.data.metaData) {
          var statusField = wf.data.metaData.statusField || ''
          var grid = statusField.split('.')[0]
          var createEvent = wf.data.metaData.createEvent
        }

        if (grid === gridId && createEvent) {
          //console.log('create event workflow call',{workflow:key, row:rowKey})
          callWf({ workflow: wf['name'], row: rowKey })
        } else {
          return
        }
      })
    },
    callWf(callData) {
      callData.dashboard = localStorage.lastDash
      //console.log('wf call data',callData)

      const functionLink = `${functionsUrl}/callTheWf`

      axios
        .post(functionLink, JSON.stringify(callData))
        .then(res => {})
        .catch(err => console.log('err'))
    },
    addRecord() {
      this.addRowInProcess = true

      $dgAddRow(this.gridId)
        .then(async rowKey => {
          this.addRowInProcess = false

          await this.insertDefaultVals(rowKey)

          if (this.fbField) {
            eventBus.$emit(`fbRowAdded-${this.gridId}`, rowKey, () => {
              $dgShowEditRowModal(this.gridId, rowKey)
            })
          } else {
            const currentQuery = JSON.stringify(this.$route.query)
            const editQuery = {
              dgEditMode: true,
              gridId: this.gridId,
              rowKey,
            }
            const query = Object.assign(JSON.parse(currentQuery), editQuery)

            this.$router.push({ path: '/dashboard/' + this.currentDash, query })
          }

          this.addWorkflow(rowKey)
          setTimeout(() => {
            this.pageClickHandler(this.pagesCount)
          }, 100)
        })
        .catch(() => {
          this.addRowInProcess = false
        })
    },
    applySort(sortCol, direction) {
      this.sortByView[this.view] = {
        sortCol: sortCol,
        sortDirection: direction,
      }
      this.sortAndFilterGridWithDebounce()
    },
    getCells(row) {
      const cells = Object.assign({}, row)
      delete cells.rowKey

      for (let cellKey in cells) {
        if (this.fbField && this.fbField === cellKey) delete cells[cellKey]
        if (this.hiddenCols.includes(cellKey)) delete cells[cellKey]
      }

      return cells
    },
    toggleFilter() {
      this.filterOn = !this.filterOn
    },
    clearFilter() {
      this.filter = {}
      this.filterBuilder = null
    },
    onFilterItemRemove(cellKey) {
      const newFilter = Object.assign({}, this.filter)
      delete newFilter[cellKey]
      this.filter = newFilter
    },
    columnVisibilityChanged(value, cellKey) {
      const data = {
        gridId: this.gridId,
        cellKey,
        value,
      }

      if (this.viewExists) data.view = this.view

      db.dataGrid.options.changeColumnVisability(data)
    },
    resetEditView() {
      const currentQuery = cloneDeep(this.$route.query)
      delete currentQuery.dgEditMode
      delete currentQuery.gridId
      delete currentQuery.rowKey

      this.$router.push({
        path: '/dashboard/' + this.currentDash,
        query: currentQuery,
      })
    },
    getColumnMinWidth(width) {
      if (!width) return false

      return isNaN(+width) ? width : `${width}px`
    },
    singular(title) {
      return singular(title)
    },
    getFieldControlInfo(cellKey) {
      return (
        this.$getDGFBControl(
          this.dataGrid_options[this.gridId].rowEditorFieldsForm,
          cellKey
        ) || {}
      )
    },
    initGridData() {
      if (!this.options) return
      if (!this.options.meta) return

      try {
        const filterBuilderString = this.getFilterBuilderString()

        const filterBuilder = filterBuilderString
          ? JSON.parse(filterBuilderString)
          : null

        this.applyFilterBuilder(filterBuilder)
      } catch (err) {
        console.error(err)
      }
    },
    insertDefaultVals(rowKey) {
      const meta = this.dataGrid_options[this.gridId].meta
      const cellKeys = Object.keys(meta)

      return new Promise(resolve => {
        const getInsert = cellKey => {
          const control = this.$getDGFBControl(
            this.options.rowEditorFieldsForm,
            cellKey
          )

          if (!control) {
            resolve()
            return
          }

          const data = {
            options: this.options,
            rowKey,
            cellKey,
          }

          if (meta[cellKey].type === 'checkbox') {
            if (control.isChecked) {
              data.value = true

              return new Promise(innerResolve => {
                db.dataGrid.data.saveCellVal(data).then(() => innerResolve())
              })
            }
          } else {
            if (control.defaultValue) {
              data.value = control.defaultValue

              return new Promise(innerResolve => {
                db.dataGrid.data.saveCellVal(data).then(() => innerResolve())
              })
            }
          }
        }

        const promises = cellKeys.map(cellKey => getInsert(cellKey))

        Promise.all(promises).then(() => {
          resolve()
        })
      })
    },
    getFilterBuilderString(reset) {
      if (this.dataGrid_filterBuilders[this.widgetKey] && !reset)
        return this.dataGrid_filterBuilders[this.widgetKey] || '{}'
      if (this.viewExists)
        return this.options.views[this.view].filterBuilder || '{}'
      return this.options.filterBuilder || '{}'
    },
    updateHandyScroll() {
      if (this.$refs.handyScroll)
        this.$refs.handyScroll.$emit('update', this.$refs.handyScroll.$el)
    },
    getInheritedOptions() {
      let gridOptions

      for (let gridId in this.dInst.inheritedData || {}) {
        const dashData = this.dInst.inheritedData[gridId]

        if (!dashData) continue
        if (!dashData.widgetsData) continue
        if (!dashData.widgetsData.dataGrid) continue
        if (!dashData.widgetsData.dataGrid[this.gridId]) continue

        gridOptions = dashData.widgetsData.dataGrid[this.gridId]
        break
      }

      this.inheritedOptions = gridOptions || null
    },
    rowAddedHandler(rowKey) {
      this.$emit('rowAdded', { gridId: this.gridId, rowKey })
    },
    rowChangedHandler(rowKey) {
      const rowData = $dataGrid(this.gridId)[rowKey]

      if (!this.gridReady) this.gridReady = []
      if (!this.filteredData) this.filteredData = []

      if (rowData) {
        const filtered = filterUtils.filterData(
          [rowData],
          this.filter,
          this.cellsMeta
        )

        if (filtered.length) {
          const idx1 = findIndex(this.gridReady, { rowKey })
          const idx2 = findIndex(this.filteredData, { rowKey })

          if (~idx1) this.gridReady[idx1] = rowData
          else this.gridReady.push(rowData)
          if (~idx2) this.filteredData[idx2] = rowData
          else this.filteredData.push(rowData)
        }
      } else {
        const idx1 = findIndex(this.gridReady, { rowKey })
        const idx2 = findIndex(this.filteredData, { rowKey })

        if (~idx1) this.gridReady.splice(idx1, 1)
        if (~idx2) this.filteredData.splice(idx2, 1)
      }
    },
  },
  async created() {
    this.dInst = this.$instByClass(this, 'dashboard-component')

    this.getInheritedOptions()

    // apply default filter
    if (this.options && this.options.defaultSort) {
      this.sortByView[this.view] = {
        sortCol: this.options.defaultSort.column,
        sortDirection: this.options.defaultSort.direction,
      }
    }

    this.$on('dgColumnOrderChange', newMeta => {
      const data = {
        gridId: this.gridId,
        newMeta,
      }

      if (this.viewExists) data.view = this.view

      db.dataGrid.options.setNewColumnOrder(data)
    })

    this.initGridData()

    eventBus.$on(`dgGridChanged-${this.gridId}`, this.sortAndFilterGrid)
    eventBus.$on(`dgRowAdded-${this.gridId}`, this.rowAddedHandler)
    eventBus.$on(`dgRowChanged-${this.gridId}`, this.rowChangedHandler)

    setTimeout(() => {
      this.updateHandyScroll()
    }, 2000)
  },
  beforeDestroy() {
    eventBus.$off(`dgGridChanged-${this.gridId}`, this.sortAndFilterGrid)
    eventBus.$off(`dgRowAdded-${this.gridId}`, this.rowAddedHandler)
    eventBus.$off(`dgRowChanged-${this.gridId}`, this.rowChangedHandler)

    if (this.$refs.handyScroll)
      this.$refs.handyScroll.$emit('destroy', this.$refs.handyScroll.$el)
  },
}
</script>

<style lang="scss">
@import './styles/index.scss';
.multiselect__content-wrapper {
  &._detached {
    z-index: 99999;
  }
}
</style>
